import type { JsonValue } from 'ProdantixSdk/core/types';

export type FlagOp = 'eq' | 'neq' | 'in' | 'not_in' | 'contains' | 'gt' | 'gte' | 'lt' | 'lte' | 'is_set' | 'is_not_set' | 'in_cohort' | 'not_in_cohort';

export interface FlagCondition {
  attribute: string;
  op: FlagOp;
  value?: JsonValue;
}

export interface FlagEvalContext {
  attributes?: Record<string, JsonValue>;
  // The cohorts the distinct id belongs to, read from the membership store
  // at decision time: what the two cohort operators test.
  cohorts?: string[];
  distinctId: string;
  properties?: Record<string, JsonValue>;
}

// The sentinel for an absent attribute (JS `undefined`), distinct from a present
// `null`. Kept internal so callers only pass real JSON values.
const ABSENT = Symbol('absent');
type Resolved = JsonValue | typeof ABSENT;

// Reserved `distinct_id`, then own `properties` (present even if null), then
// `attributes`, else absent — mirrors the server's `resolve`.
function resolve(attribute: string, ctx: FlagEvalContext): Resolved {
  if (attribute === 'distinct_id') {
    return ctx.distinctId;
  }
  const properties = ctx.properties ?? {};
  if (Object.prototype.hasOwnProperty.call(properties, attribute)) {
    return properties[attribute];
  }
  const attributes = ctx.attributes ?? {};
  if (Object.prototype.hasOwnProperty.call(attributes, attribute)) {
    return attributes[attribute];
  }
  return ABSENT;
}

// `String(x)` with the absent sentinel rendering as JS `String(undefined)`.
function toStr(x: Resolved): string {
  return x === ABSENT ? 'undefined' : String(x);
}

// `Number(x)` with the absent sentinel rendering as JS `Number(undefined)` (NaN).
function toNum(x: Resolved): number {
  return x === ABSENT ? Number.NaN : Number(x);
}

function isSet(x: Resolved): boolean {
  return x !== ABSENT && x !== null && x !== '';
}

function inSet(actual: Resolved, value: JsonValue | undefined): boolean {
  if (!Array.isArray(value)) {
    return false;
  }
  const needle = toStr(actual);
  return value.map(item => String(item)).includes(needle);
}

export function matches(condition: FlagCondition, ctx: FlagEvalContext): boolean {
  const actual = resolve(condition.attribute, ctx);
  const value = condition.value;
  switch (condition.op) {
    case 'is_set':
      return isSet(actual);
    case 'is_not_set':
      return !isSet(actual);
    case 'eq':
      return toStr(actual) === toStr(value === undefined ? ABSENT : value);
    case 'neq':
      return toStr(actual) !== toStr(value === undefined ? ABSENT : value);
    case 'contains':
      return toStr(actual).includes(toStr(value === undefined ? ABSENT : value));
    case 'in':
      return inSet(actual, value);
    case 'not_in':
      return Array.isArray(value) ? !inSet(actual, value) : false;
    case 'gt':
      return numCmp(actual, value, (a, b) => a > b);
    case 'gte':
      return numCmp(actual, value, (a, b) => a >= b);
    case 'lt':
      return numCmp(actual, value, (a, b) => a < b);
    case 'lte':
      return numCmp(actual, value, (a, b) => a <= b);
    case 'in_cohort':
      return typeof value === 'string' && value !== '' && (ctx.cohorts ?? []).includes(value);
    case 'not_in_cohort':
      return typeof value === 'string' && value !== '' && !(ctx.cohorts ?? []).includes(value);
    default:
      return false;
  }
}

function numCmp(actual: Resolved, value: JsonValue | undefined, cmp: (a: number, b: number) => boolean): boolean {
  const a = toNum(actual);
  const b = value === undefined ? Number.NaN : toNum(value);
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return false;
  }
  return cmp(a, b);
}
